/**
 * Cross-entity cache invalidation registry.
 *
 * Backend `executeAutomatedPopulation` returns populated copies of related
 * entities embedded in many query responses (e.g. assignees with their
 * OrgFunction, OrgFunctions with department managers). When the source entity
 * changes, every cached query that holds a populated copy of it goes stale —
 * but each mutation only invalidates its own key family by default, so
 * populated data lingers until the staleTime elapses.
 *
 * `invalidateEntity` solves this by mapping each `EntityKind` to the prefix
 * keys that may contain populated copies of it, and invalidating them all.
 * The registry also carries derived aggregates — seat usage is computed from
 * the member set, so it goes stale with the entity it is derived from.
 */

import type { QueryClient, QueryKey } from '@tanstack/react-query';
import {
  artifactKeys,
  authKeys,
  dashboardKeys,
  departmentKeys,
  externalContactKeys,
  orgFunctionKeys,
  orgMemberKeys,
  organizationKeys,
  processChainKeys,
  processKeys,
  recordingDashboardKeys,
  recordingKeys,
  seatUsageKeys,
  systemKeys,
  taskKeys,
  userKeys
} from './query-keys';

export type EntityKind =
  | 'user'
  | 'orgMember'
  | 'orgFunction'
  | 'department'
  | 'system'
  | 'process'
  | 'recording';

export const ENTITY_DEPENDENTS = {
  user: [
    userKeys.all,
    orgMemberKeys.all,
    authKeys.profile(),
    processKeys.all,
    taskKeys.all,
    orgFunctionKeys.all,
    systemKeys.all,
    departmentKeys.all,
    recordingKeys.all,
    dashboardKeys.all,
    recordingDashboardKeys.all,
    organizationKeys.all
  ],
  orgMember: [
    orgMemberKeys.all,
    userKeys.all,
    authKeys.profile(),
    processKeys.all,
    taskKeys.all,
    orgFunctionKeys.all,
    systemKeys.all,
    departmentKeys.all,
    recordingKeys.all,
    dashboardKeys.all,
    recordingDashboardKeys.all,
    organizationKeys.all,
    seatUsageKeys.organization()
  ],
  // OrgFunction is the unified org-structure entity (the former Team and Role):
  // populated copies surface in org members, processes, tasks, departments and
  // organization query families, so invalidate that combined set.
  orgFunction: [
    orgFunctionKeys.all,
    orgMemberKeys.all,
    userKeys.all,
    processKeys.all,
    taskKeys.all,
    systemKeys.all,
    departmentKeys.all,
    organizationKeys.all
  ],
  department: [
    departmentKeys.all,
    orgMemberKeys.all,
    userKeys.all,
    orgFunctionKeys.all,
    processKeys.all,
    taskKeys.all,
    systemKeys.all,
    organizationKeys.all
  ],
  system: [
    systemKeys.all,
    processKeys.all,
    taskKeys.all,
    artifactKeys.all,
    externalContactKeys.all
  ],
  process: [
    processKeys.all,
    taskKeys.all,
    dashboardKeys.all,
    recordingKeys.all,
    recordingDashboardKeys.all,
    processChainKeys.all,
    organizationKeys.all
  ],
  // Note: `useUpdateStepRecording` deliberately does NOT use the `recording`
  // kind — it relies on an optimistic-update flow with `setQueryData` and a
  // `refetchType: 'none'` invalidation. Broad invalidation here would refetch
  // and clobber the optimistic value. Consult that mutation before migrating.
  recording: [
    recordingKeys.all,
    processKeys.all,
    taskKeys.all,
    dashboardKeys.all,
    recordingDashboardKeys.all
  ]
} as const satisfies Record<EntityKind, readonly QueryKey[]>;

/**
 * Invalidate every query family that may hold populated copies of the given
 * entity kind(s). Call from a mutation's `onSuccess` (or `onSettled` if the
 * mutation uses optimistic updates, to avoid invalidating mid-rollback).
 * Return (or await) the promise when callers of `mutateAsync` must observe
 * the refetched data — e.g. the setup wizard's flush-and-clear.
 *
 * `awaitKey` narrows the returned promise to that one dependent's refetch;
 * the others still refetch, in the background.
 *
 * Broad-prefix invalidation only refetches *active* queries by default in
 * TanStack Query v5; inactive queries flip to stale and refetch on next mount.
 */
export function invalidateEntity<K extends EntityKind>(
  queryClient: QueryClient,
  kinds: K | K[],
  { awaitKey }: { awaitKey?: (typeof ENTITY_DEPENDENTS)[K][number] } = {}
): Promise<void> {
  const list = Array.isArray(kinds) ? kinds : [kinds];
  const keys = new Map<string, QueryKey>();
  for (const kind of list) {
    for (const key of ENTITY_DEPENDENTS[kind]) keys.set(JSON.stringify(key), key);
  }
  const awaitFingerprint = awaitKey && JSON.stringify(awaitKey);
  const invalidations: Promise<void>[] = [];
  let awaited: Promise<void> | undefined;
  for (const [fingerprint, key] of keys) {
    const invalidation = queryClient.invalidateQueries({ queryKey: key });
    invalidations.push(invalidation);
    if (fingerprint === awaitFingerprint) awaited = invalidation;
  }
  return awaited ?? Promise.all(invalidations).then(() => undefined);
}
